import { useState, useEffect, useCallback, useMemo } from 'react';
import { postJson } from '../api.js';
import { useCamSeen, CAM_SEEN_TITLE, CAM_PROBABLE_TITLE } from '../utils/plates.js';
import { cachedFetch, bustPrefix } from '../dataCache.js';
import { supabase } from '../supabaseClient.js';
import { bustTipCaches, ISSUE_LABEL, CRITICAL, COLS, fmtDate, fmtTime, getJson } from './review/helpers.js';
import PageHeader from './review/PageHeader.jsx';
import AdminToolsCard from './review/AdminToolsCard.jsx';
import CoverageCard from './review/CoverageCard.jsx';
import ReviewTabs from './review/ReviewTabs.jsx';
import SearchBar from './review/SearchBar.jsx';
import StatusFilters from './review/StatusFilters.jsx';
import PendingDateRange from './review/PendingDateRange.jsx';
import SelectionBar from './review/SelectionBar.jsx';
import ApprovedPager from './review/ApprovedPager.jsx';
import TipsTable from './review/TipsTable.jsx';
import PhotoModal from './review/PhotoModal.jsx';
import CorrectionModal from './review/CorrectionModal.jsx';
import RawRowModal from './review/RawRowModal.jsx';
import DangerZone from './review/DangerZone.jsx';
import ResetModal from './review/ResetModal.jsx';
import AddPlateModal from './review/AddPlateModal.jsx';
import EditApprovedModal from './review/EditApprovedModal.jsx';
import HistoryModal from './review/HistoryModal.jsx';
import InboxModal from './review/InboxModal.jsx';
import BulkEditModal from './review/BulkEditModal.jsx';
import UnapproveModal from './review/UnapproveModal.jsx';
import HoverPreview from './review/HoverPreview.jsx';

// Review page: where tips (incoming truck loads from the Google sheet) are
// checked and approved for billing. This file holds the page's data and
// actions; everything visible is drawn by the pieces in ./review/ (one
// file per toolbar, table and pop-up). Tabs: Pending review (new sheet
// rows), Approved (billed tips, paged from the server), Unmatched
// (old sheet rows with no approved match, admin only).
export default function Review({ canWrite = true }) {
  // ── Screen state: current tab, busy flags, message, selected rows, open photo/raw pop-ups ──
  const [tab, setTab] = useState('pending');
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [photoRow, setPhotoRow] = useState(null);
  const [rawRow, setRawRow] = useState(null);
  const [hoverPhoto, setHoverPhoto] = useState(null);  // { url, plate, x, y }
  // "📷 verified" mark: camera read this plate on the tip's day
  // (synced Verkada history, last 92 days).
  const { isSeen: camSeen, isProbable: camProbable } = useCamSeen(
    new Date(Date.now() - 92 * 86400e3).toISOString().slice(0, 10),
    new Date().toISOString().slice(0, 10));
  // ── Correct-a-pending-tip pop-up + dropdown lists (customers, plates, tank sizes) ──
  const [editRow, setEditRow] = useState(null);  // row currently being corrected
  const [editFields, setEditFields] = useState(null);  // {licensePlate, customer, tankSize}
  const [refLists, setRefLists] = useState({ customers: [], tankSizes: [], plates: [] });
  // ── Projects: per-project rate overrides; assign tips to a project (one or many) ──
  // Customer projects (schema-046 rate overrides). Loaded once; drives
  // the Project column dropdown + bulk-assign. Maps rebuilt on load.
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    getJson('/api/customer-projects?active=true')
      .then(j => setProjects(j.projects || []))
      .catch(() => {});
  }, []);
  const projectsById = useMemo(() => new Map(projects.map(p => [Number(p.id), p])), [projects]);
  const projectsByCustomer = useMemo(() => {
    const m = new Map();
    for (const p of projects) {
      const k = String(p.customer_canonical_name || '').toLowerCase().trim();
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    }
    return m;
  }, [projects]);
  const [projBusy, setProjBusy] = useState(false);
  async function assignProject(rowIds, projectIdOrNull) {
    setProjBusy(true); setMsg(null);
    try {
      const source = tab === 'approved' ? 'excel' : 'google';
      const r = await postJson('/api/tips/assign-project', {
        tipIds: rowIds, projectId: projectIdOrNull, source
      });
      let m = (projectIdOrNull == null ? 'Removed project from ' : 'Assigned project to ') + r.updated + ' tip(s).';
      if (r.skipped > 0) m += ' ' + r.skipped + ' skipped — tip customer does not match the project’s customer.';
      setMsg(m);
      // Optimistic local update so the dropdown reflects the assignment
      // IMMEDIATELY, independent of any cache staleness on the refetch.
      // Skipped (mismatched) tips are excluded from the patch.
      const skippedIds = new Set((r.mismatches || []).map(x => x.tipId));
      const okIds = new Set(rowIds.filter(id => !skippedIds.has(id)));
      const patchTip = t => okIds.has(t._id) ? { ...t, project_id: projectIdOrNull } : t;
      setPending(prev => ({ ...prev, tips: (prev.tips || []).map(patchTip) }));
      setUnmatched(prev => ({ ...prev, tips: (prev.tips || []).map(patchTip) }));
      setAppRows(prev => (prev || []).map(patchTip));
      // Bust caches but DO NOT refetch here. The immediate refresh()
      // was overwriting the (correct) optimistic patch with a stale
      // cached page from another Function instance whose L1 hadn't
      // expired yet -- symptom: "assigns and then disappears" while the
      // DB actually held the assignment. The optimistic patch IS the
      // confirmed server state (updated/mismatch counts came from the
      // write response); the next natural reload gets fresh data since
      // the response cache was busted server-side.
      bustTipCaches();
    } catch (e) {
      setMsg('Project assignment failed: ' + e.message);
    } finally { setProjBusy(false); }
  }
  function bulkAssignProject() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    // All selected tips must share ONE customer so the project list is
    // unambiguous.
    const selRows = visibleRows.filter(r => selected.has(r._id));
    const custs = Array.from(new Set(selRows.map(r => String(r.customer || '').toLowerCase().trim()).filter(Boolean)));
    if (custs.length !== 1) {
      alert('Select tips from a single customer to bulk-assign a project.\n\nSelected tips span ' + custs.length + ' customers.');
      return;
    }
    const opts = projectsByCustomer.get(custs[0]) || [];
    if (opts.length === 0) {
      alert('No active projects for "' + selRows[0].customer + '".\n\nAdd one on the customer card: Reference → Customers → Edit → Projects.');
      return;
    }
    const menu = opts.map((p, i) => (i + 1) + ') ' + p.name + (p.code ? ' [' + p.code + ']' : '')).join('\n');
    const pick = prompt('Assign ' + ids.length + ' selected tip(s) to which project?\n\n' + menu + '\n0) Remove project assignment\n\nEnter number:');
    if (pick == null) return;
    const n = parseInt(pick, 10);
    if (n === 0) { assignProject(ids, null); return; }
    if (!Number.isFinite(n) || n < 1 || n > opts.length) { alert('Invalid choice.'); return; }
    assignProject(ids, opts[n - 1].id);
  }
  // ── Signed-in user + state for the add-plate, edit-approved, history, unapprove and bulk-edit pop-ups ──
  const [me, setMe] = useState(null);
  const [addPlateRow, setAddPlateRow] = useState(null);   // row prefilling the add-plate modal
  const [addPlateBusy, setAddPlateBusy] = useState(false);
  const [editApprovedRow, setEditApprovedRow] = useState(null);   // tips_excel row being edited
  const [editApprovedFields, setEditApprovedFields] = useState(null);
  const [editApprovedReason, setEditApprovedReason] = useState('');
  const [editApprovedBusy, setEditApprovedBusy] = useState(false);
  const [historyRow, setHistoryRow] = useState(null);             // row whose audit log we're viewing
  const [historyEntries, setHistoryEntries] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [unapproveRow, setUnapproveRow] = useState(null);
  const [unapproveReason, setUnapproveReason] = useState('');
  const [unapproveBusy, setUnapproveBusy] = useState(false);
  // Bulk edit (customer + tank size) on selected rows, with one-shot undo.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCustomer, setBulkCustomer] = useState('');
  const [bulkTankSize, setBulkTankSize] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [lastBulkBatch, setLastBulkBatch] = useState(null); // { batchId, count, label } once committed
  const [undoBusy, setUndoBusy] = useState(false);

  // ── Actions: send an approved tip back to Pending ──
  async function confirmUnapprove() {
    if (!unapproveRow || !unapproveReason.trim()) return;
    setUnapproveBusy(true);
    try {
      const r = await postJson('/api/excel/unapprove', { id: unapproveRow._id, reason: unapproveReason.trim() });
      const id = unapproveRow._id;
      setAppRows(prev => prev.filter(rr => rr._id !== id));
      setAppTotal(prev => Math.max(0, prev - 1));
      setMsg('Unapproved row ' + id + (r.googleRevived ? ' — ' + r.googleRevived + ' sheet row(s) flipped back to pending' : ' — no sheet origin to revive') + '.');
      setUnapproveRow(null);
      setUnapproveReason('');
      // Refresh in the background to pick up the now-pending Google rows
      setTimeout(() => { refresh().catch(() => {}); }, 200);
    } catch (e) {
      setMsg('Unapprove failed: ' + e.message);
    } finally { setUnapproveBusy(false); }
  }
  // ── Sheet-changes inbox: edits made in the Google sheet after a tip was approved ──
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxEntries, setInboxEntries] = useState(null);
  const [inboxCount, setInboxCount] = useState(0);
  const [inboxBusy, setInboxBusy] = useState(false);

  async function loadInboxHeadline() {
    try {
      const j = await getJson('/api/audit-log?source=sheet-detected&limit=1&headline=1');
      setInboxCount((j.unreadCounts && j.unreadCounts.sheetDetected7d) || 0);
    } catch (_) {}
  }
  async function openInbox() {
    setInboxOpen(true);
    setInboxBusy(true);
    try {
      const j = await getJson('/api/audit-log?source=sheet-detected&limit=200');
      setInboxEntries(j.audits || []);
    } catch (e) { setInboxEntries([]); setMsg('Inbox load failed: ' + e.message); }
    finally { setInboxBusy(false); }
  }
  useEffect(() => { loadInboxHeadline(); const t = setInterval(loadInboxHeadline, 60_000); return () => clearInterval(t); }, []);

  // ── Actions: edit an approved tip, view its history, register a new plate, correct a pending tip ──
  function openApprovedEditor(r) {
    setEditApprovedFields({
      licensePlate: r.licensePlate || '',
      customer:     r.customer || '',
      tankSize:     r.tankSize != null ? String(r.tankSize) : '',
      pctFilled:    r.pctFilled != null ? String(r.pctFilled) : '',
      pctSolids:    r.pctSolids != null ? String(r.pctSolids) : '',
      site:         r.site || '',
      contractor:   r.contractor || '',
      truckNumber:  r.truckNumber || '',
      driver:       r.driver || ''
    });
    setEditApprovedReason('');
    setEditApprovedRow(r);
  }
  async function saveApprovedEdit() {
    if (!editApprovedRow) return;
    if (!editApprovedReason.trim()) { setMsg('Please enter a reason for the edit (audit trail).'); return; }
    setEditApprovedBusy(true);
    try {
      const r = await postJson('/api/excel/edit', {
        id: editApprovedRow._id,
        fields: editApprovedFields,
        reason: editApprovedReason.trim()
      });
      if (r.noChange) {
        setMsg('No fields changed — no edit applied.');
      } else {
        setMsg('Edited approved row · changed: ' + (r.changed || []).join(', '));
        // Optimistic patch: update the appRows row locally so the edit shows immediately
        const id = editApprovedRow._id;
        const patch = { ...editApprovedFields };
        setAppRows(prev => prev.map(rr => rr._id !== id ? rr : {
          ...rr,
          licensePlate: patch.licensePlate || rr.licensePlate,
          customer:     patch.customer     || rr.customer,
          tankSize:     patch.tankSize     !== '' && patch.tankSize  != null ? Number(patch.tankSize)  : rr.tankSize,
          pctFilled:    patch.pctFilled    !== '' && patch.pctFilled != null ? Number(patch.pctFilled) : rr.pctFilled,
          pctSolids:    patch.pctSolids    !== '' && patch.pctSolids != null ? Number(patch.pctSolids) : rr.pctSolids,
          site:         patch.site         || rr.site,
          contractor:   patch.contractor   || rr.contractor,
          truckNumber:  patch.truckNumber  || rr.truckNumber,
          driver:       patch.driver       || rr.driver,
          _editedAfterApproval: true,
          _updatedAt: new Date().toISOString()
        }));
      }
      setEditApprovedRow(null); setEditApprovedFields(null); setEditApprovedReason('');
    } catch (e) { setMsg('Edit failed: ' + e.message); }
    finally { setEditApprovedBusy(false); }
  }

  async function openHistory(r) {
    setHistoryRow(r);
    setHistoryEntries(null);
    setHistoryBusy(true);
    try {
      const j = await getJson('/api/audit-log?row_id=' + encodeURIComponent(r._id) + '&limit=100');
      setHistoryEntries(j.audits || []);
    } catch (e) {
      setHistoryEntries([]);
      setMsg('History load failed: ' + e.message);
    } finally { setHistoryBusy(false); }
  }
  useEffect(() => { getJson('/api/whoami').then(setMe).catch(() => setMe(null)); }, []);

  async function confirmAddPlate() {
    if (!addPlateRow) return;
    // Snapshot at entry so a backdrop click or fast keystroke after we
    // start the await doesn't yank addPlateRow out from under us. Was
    // the same stale-closure pattern that bit saveApprovedEdit (task #16).
    const snap = addPlateRow;
    const plateNorm = String(snap.licensePlate || '').toUpperCase().replace(/\s+/g, '');
    if (!plateNorm) { setMsg('Add plate failed: plate is required'); return; }
    if (!snap.customer || !String(snap.customer).trim()) {
      setMsg('Add plate failed: customer is required');
      return;
    }
    // Validate tankSize CLIENT-SIDE before sending. The server already
    // 400s on NaN/out-of-range now, but JSON.stringify(NaN) emits 'null'
    // — without this guard, "ten" silently saves as tank_size=NULL and
    // tank_size_mismatch can never fire on future loads. Surface the
    // error to the user instead.
    let tankSize = null;
    if (snap.tankSize !== '' && snap.tankSize != null) {
      tankSize = Number(snap.tankSize);
      if (!Number.isFinite(tankSize)) {
        setMsg('Add plate failed: tank size must be a number (got: ' + JSON.stringify(snap.tankSize) + ')');
        return;
      }
      if (tankSize < 1 || tankSize > 50) {
        setMsg('Add plate failed: tank size must be between 1 and 50 m3 (got: ' + tankSize + ')');
        return;
      }
    }
    setAddPlateBusy(true);
    try {
      const body = {
        plate: plateNorm,
        tankSize,
        customer: String(snap.customer).trim(),
        comment: 'Added from Pending Review by ' + (me?.userDetails || 'unknown') + ' on ' + new Date().toISOString().slice(0,10) + (snap.truckNumber ? ' · truck #' + snap.truckNumber : '')
      };
      const r = await fetch('/api/plates', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      if (!r.ok) throw new Error(r.status + ' ' + (await r.text()));
      setMsg('Added plate ' + plateNorm + ' to registry (customer: ' + (snap.customer || '—') + ', tank: ' + (tankSize != null ? tankSize : '—') + 'm³).');

      // Bust frontend dataCache so the next reload doesn't show stale
      // unknown_plate badges from the pre-save localStorage payload.
      try {
        bustPrefix('/api/google/tips');
        bustPrefix('/api/tips/list');
        bustPrefix('/api/reference/lists');
      } catch (_) { /* best-effort */ }

      // Optimistic: clear 'unknown_plate' from every visible row with this plate
      const origNorm  = String(snap._origPlate || '').toUpperCase().replace(/\s+/g, '');
      const targets = new Set([plateNorm, origNorm].filter(Boolean));
      const clearUnknown = (t) => {
        // Match the row against the registered plate AND its original
        // (pre-edit) form, on either the parsed licensePlate or any
        // raw-form plate-ish field. Without this, header drift between
        // parser and raw means we added 'AS9501' to the registry but
        // the row still says unknown because t.licensePlate is blank.
        const tParsed = String(t.licensePlate || '').toUpperCase().replace(/\s+/g, '');
        let tRaw = '';
        if (t._raw) {
          for (const [k, v] of Object.entries(t._raw)) {
            if (/plate(?!.*photo)/i.test(k) && v != null && String(v).trim() !== '') {
              tRaw = String(v).toUpperCase().replace(/\s+/g, '');
              break;
            }
          }
        }
        if (!targets.has(tParsed) && !targets.has(tRaw)) return t;
        return { ...t, _issues: (t._issues || []).filter(i => i !== 'unknown_plate'),
                       _registeredCustomer: snap.customer || null,
                       _registryTankSize: tankSize != null ? tankSize : t._registryTankSize };
      };
      // Helper: only return a new array if at least one row changed.
      // clearUnknown returns the SAME reference for rows that don't
      // match, so we can detect "no work to do" by reference-compare
      // and skip the state update entirely. Avoids a 100-row DataTable
      // re-render when adding a plate that's only on one row.
      const mapIfChanged = (rows) => {
        const next = rows.map(clearUnknown);
        return next.some((r, i) => r !== rows[i]) ? next : rows;
      };
      setPending(prev => {
        const tips = mapIfChanged(prev.tips);
        return tips === prev.tips ? prev : { ...prev, tips };
      });
      setUnmatched(prev => {
        const tips = mapIfChanged(prev.tips);
        return tips === prev.tips ? prev : { ...prev, tips };
      });
      // Approved tab too — a finance user might be adding the plate AFTER
      // the row was already approved. Patches only the current page; the
      // bustPrefix above ensures other pages refetch on navigation.
      setAppRows(prev => mapIfChanged(prev));

      // Refresh reference lists so the Add modal also reflects the new plate
      getJson('/api/reference/lists').then(setRefLists).catch(() => {});
      setAddPlateRow(null);
    } catch (e) {
      setMsg('Add plate failed: ' + e.message);
    } finally {
      setAddPlateBusy(false);
    }
  }

  function openEditor(r) {
    setEditFields({
      licensePlate: r.licensePlate || '',
      customer:     r.customer || '',
      tankSize:     r.tankSize != null ? String(r.tankSize) : ''
    });
    setEditRow(r);
  }
  async function saveCorrection() {
    if (!editRow) return;
    const editingId = editRow._id;
    const newFields = { ...editFields };
    setBusy(true);
    try {
      const r = await postJson('/api/google/correct', { id: editingId, fields: newFields });
      // Optimistic in-place patch so the corrected badge + new values
      // appear immediately, no page-refresh required.
      const nowIso = new Date().toISOString();
      const patch = (t) => t._id !== editingId ? t : ({
        ...t,
        licensePlate: r.applied && r.applied.licensePlate != null ? r.applied.licensePlate : t.licensePlate,
        customer:     r.applied && r.applied.customer     != null ? r.applied.customer     : t.customer,
        tankSize:     r.applied && Object.prototype.hasOwnProperty.call(r.applied,'tankSize') ? r.applied.tankSize : t.tankSize,
        _correctedAt: nowIso,
        _correctedBy: 'you (pending sync)',
        _originalFields: t._originalFields || (r.original || null),
        // Server re-ran detectIssues() against the corrected row; use the
        // fresh list so the Status pill flips from Critical → Clean (or
        // narrows to whatever issues remain).
        _issues: Array.isArray(r.issues) ? r.issues : (t._issues || [])
      });
      setPending(prev => ({ ...prev, tips: prev.tips.map(patch) }));
      setUnmatched(prev => ({ ...prev, tips: prev.tips.map(patch) }));
      setMsg('Saved correction for ' + (newFields.licensePlate || editingId));
      setEditRow(null); setEditFields(null);
      // Quietly reconcile with server in the background.
      setTimeout(() => { refresh().catch(() => {}); }, 100);
    } catch (e) { setMsg('Save failed: ' + e.message); }
    finally { setBusy(false); }
  }

  // ── The tip lists themselves (pending / unmatched / approved page) + sort, filter and date settings ──
  const [pending, setPending] = useState({ tips: [], counts: { pending: 0, approved: 0, matched: 0, unmatched: 0, unmatchedHistorical: 0, total: 0 } });
  const [unmatched, setUnmatched] = useState({ tips: [] });

  const [appRows, setAppRows] = useState([]);
  const [appTotal, setAppTotal] = useState(0);
  // Server-computed status counts for the Approved tab. Comes back from
  // /api/tips/list along with rows so the filter buttons show accurate
  // counts across the FULL approved dataset, not just the current page.
  const [appStatusCounts, setAppStatusCounts] = useState(null);
  const [appOffset, setAppOffset] = useState(0);
  const [appLimit] = useState(100);
  const [appSort, setAppSort] = useState({ key: 'date', dir: 'desc' });
  const [appFrom, setAppFrom] = useState('');
  const [appTo, setAppTo] = useState('');
  const [pendSort, setPendSort] = useState({ key: 'date', dir: 'desc' });
  const [statusFilter, setStatusFilter] = useState('all');   // 'all'|'clean'|'warning'|'critical'|<issue-key>
  const [searchText, setSearchText] = useState('');
  // Date-range filter for Pending review + Unmatched. Client-side (both
  // tabs already have the full row set in memory). Empty string = no bound.
  const [pendFrom, setPendFrom] = useState('');
  const [pendTo,   setPendTo]   = useState('');

  // ── Admin: sheet-vs-database coverage check ──
  // Coverage check — compares Google Sheet row counts per month against
  // tips_google. Flags months where the sheet has more rows than DB
  // (silently dropped during the incremental sync). Admin-only.
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState(null);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const loadCoverage = useCallback(async () => {
    setCoverageLoading(true);
    setCoverageError(null);
    try {
      const r = await getJson('/api/google/coverage-check?from=2026-05');
      setCoverage(r);
    } catch (e) { setCoverageError(e.message); }
    finally { setCoverageLoading(false); }
  }, []);
  useEffect(() => {
    if (me && me.isAdmin && !coverage && !coverageLoading) loadCoverage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me && me.isAdmin]);

  // ── Loading: pending, unmatched (on demand), approved page; refresh = reload the current tab ──
  const loadPending = useCallback(async () => {
    const fresh = await cachedFetch('/api/google/tips?decision=pending&limit=5000&fast=1', {
      ttl: 30_000,
      onUpdate: setPending,
      onRefreshStart: () => setRefreshing(true),
      onRefreshEnd: () => setRefreshing(false)
    });
    setPending(fresh);
  }, []);
  // Lazy: don't fetch unmatched on every mount. It's a 1MB payload only
  // visible to admins on the unmatched tab. The tab-switch effect below
  // triggers the load on demand.
  const loadUnmatched = useCallback(async () => {
    const fresh = await cachedFetch('/api/google/tips?decision=unmatched&limit=10000', {
      ttl: 60_000, onUpdate: setUnmatched
    });
    setUnmatched(fresh);
  }, []);
  const loadApproved = useCallback(async () => {
    const qs = new URLSearchParams({ offset: String(appOffset), limit: String(appLimit), sort: appSort.key, dir: appSort.dir });
    if (appFrom) qs.set('from', appFrom);
    if (appTo)   qs.set('to', appTo);
    // Send search term so it scans the FULL 29K-row dataset on the server,
    // not just the current 100-row page. Without this, partial-plate
    // searches like 'AS9' returned nothing if the match wasn't on page 1.
    const searchTerm = (searchText || '').trim();
    if (searchTerm) qs.set('q', searchTerm);
    // Pass the issue filter so the server scopes the result set + status
    // counts to matching rows. Only sent when in the Approved tab (the
    // statusFilter state is shared with pending — sending it always
    // would over-narrow even when the user is on Pending).
    if (tab === 'approved' && statusFilter && statusFilter !== 'all') {
      qs.set('issue', statusFilter);
    }
    const a = await getJson('/api/tips/list?' + qs.toString());
    setAppRows(a.rows || []);
    setAppTotal(a.total || 0);
    setAppStatusCounts(a.statusCounts || null);
  }, [appOffset, appLimit, appSort, appFrom, appTo, searchText, statusFilter, tab]);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const tasks = [loadPending(), loadApproved()];
      // Unmatched is admin-only diagnostic — load it only if the tab is open.
      // Skipping saves ~1MB on every page load for everyone else.
      if (tab === 'unmatched') tasks.push(loadUnmatched());
      await Promise.all(tasks);
    } catch (e) { setMsg('Load failed: ' + e.message); }
    finally { setBusy(false); }
  }, [loadPending, loadUnmatched, loadApproved, tab]);
  useEffect(() => { refresh(); }, [refresh]);
  // When the status filter changes on the Approved tab, reset to page 1
  // so the user lands on the first page of the filtered subset.
  useEffect(() => {
    if (tab === 'approved') setAppOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, tab]);
  // Load the reference dropdown data (customers, plates, sites, contractors,
  // drivers, trucks, tank sizes) once on mount. Without this, every dropdown
  // in the edit / bulk-edit / correct modals renders empty.
  useEffect(() => {
    getJson('/api/reference/lists').then(setRefLists).catch(() => {});
  }, []);

  // Live refresh (2026-10-07 security fix): the browser no longer reads
  // tip rows through the public Supabase key. It listens to a tiny
  // signal row (realtime_signals 'tips' — a timestamp, no data) that a
  // database trigger bumps whenever tips change, then re-fetches the
  // pending list through the normal authenticated API.
  useEffect(() => {
    let timer = null;
    const reload = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        bustPrefix('/api/google/tips');
        loadPending().catch(() => {});
        if (unmatched && unmatched.tips && unmatched.tips.length) loadUnmatched().catch(() => {});
      }, 600);
    };
    const channel = supabase
      .channel('review_tips_signal')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'realtime_signals', filter: 'name=eq.tips' }, reload)
      .subscribe();
    return () => { if (timer) clearTimeout(timer); supabase.removeChannel(channel); };
  }, [loadPending, loadUnmatched]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Admin: full reset (typed confirmation + snapshot), full re-sync, Google sync ──
  // T1.2 — Full Reset is now gated by a typed-confirmation modal AND takes
  // an automatic snapshot of tips_excel + tips_google before wiping.
  const [resetModal, setResetModal] = useState(null);  // null | 'open' | 'snapshotting' | 'wiping' | 'done'
  const [resetTyped, setResetTyped] = useState('');
  const [resetSnapshot, setResetSnapshot] = useState(null);  // { archives, rowCounts }

  function openResetModal() {
    setResetTyped('');
    setResetSnapshot(null);
    setResetModal('open');
  }
  async function runReset() {
    if (resetTyped !== 'RESET') return;
    setBusy(true);
    try {
      setResetModal('snapshotting');
      setMsg('Snapshotting current data before wipe…');
      const snap = await postJson('/api/snapshot/create');
      setResetSnapshot(snap);
      setMsg('Snapshot saved as ' + snap.archives.tips_excel + ' (' + snap.rowCounts.tips_excel + ' rows) + ' + snap.archives.tips_google + ' (' + snap.rowCounts.tips_google + ' rows). Starting wipe…');

      setResetModal('wiping');
      let t = 0;
      while (true) { setMsg('Wiping Google… ' + t); const r = await postJson('/api/google/purge'); t += r.deleted || 0; if (r.done) break; }
      let te = 0;
      while (true) { setMsg('Wiping Approved… ' + te); const r = await postJson('/api/tips/purge'); te += r.deleted || 0; if (r.done) break; }
      let off = 0, mig = 0;
      while (true) { const r = await postJson('/api/tips/migrate?offset=' + off + '&limit=2000'); mig += r.processed || 0; setMsg('Approved seed ' + mig + ' / ' + r.totalSeed + ' (' + r.progressPct + '%)'); if (r.done) break; off = r.nextOffset; }
      let go = 0, apr = 0, pnd = 0;
      while (true) { const r = await postJson('/api/google/import?incremental=false&offset=' + go + '&limit=2000'); apr += r.autoApproved || 0; pnd += r.queuedPending || 0; setMsg('Google ' + r.nextOffset + ' / ' + r.totalSheet + ' (' + r.progressPct + '%)'); if (r.done) break; go = r.nextOffset; }
      setMsg('Reset complete. Approved: ' + mig + ' · Sheet: ' + apr + ' approved + ' + pnd + ' pending. Snapshot retained: ' + snap.archives.tips_excel);
      setResetModal('done');
      bustTipCaches(); await refresh();
    } catch (e) {
      setMsg('Reset failed: ' + e.message + (resetSnapshot ? ' — snapshot ' + resetSnapshot.archives.tips_excel + ' still intact.' : ''));
      setResetModal(null);
    }
    finally { setBusy(false); }
  }
  async function handleFullResync() {
    if (!confirm('Run a full Google Sheet re-pull?\n\nThis re-walks every row from the sheet (UPSERT — duplicates are no-ops). Use when the incremental Refresh button doesn\'t pick up new rows because the cursor has fallen behind. Takes ~10-30 seconds.')) return;
    setBusy(true); setMsg('Full re-pull of Google Sheet…');
    try {
      const r = await postJson('/api/google/resync');
      setMsg('Full re-pull complete: ' + r.totalSheet + ' sheet rows · ' + r.upserted + ' upserted · ' + (r.preservedApproved||0) + ' approvals preserved · ' + (r.queuedPending||0) + ' pending · cursor reset to row ' + r.cursorReset + ' (' + r.durationSeconds + 's).');
      bustTipCaches(); await refresh();
    } catch (e) { setMsg('Resync failed: ' + e.message); }
    finally { setBusy(false); }
  }
  async function handleGoogleSync() {
    setBusy(true); setMsg('Pulling new sheet rows…');
    try {
      const r = await postJson('/api/google/import?incremental=true');
      setMsg('Sheet sync complete: ' + r.chunkSize + ' new rows · ' + (r.autoApproved||0) + ' auto-approved · ' + (r.queuedPending||0) + ' pending · cursor at row ' + r.cursorAt);
      bustTipCaches(); await refresh();
    } catch (e) { setMsg('Sync failed: ' + e.message); }
    finally { setBusy(false); }
  }
  // ── Actions: approve the selected tips ──
  async function handleApproveSelected() {
    if (selected.size === 0) { setMsg('Select at least one row.'); return; }
    if (!confirm('Approve ' + selected.size + ' tip(s)? Those without an existing Approved match will be added; Approved rows are never overwritten.')) return;
    const ids = Array.from(selected);
    const idSet = new Set(ids);
    // Snapshot the source rows BEFORE we filter them out of pending/unmatched —
    // we use them to build optimistic Approved-tab entries below.
    const sourceRows = [...pending.tips, ...unmatched.tips].filter(t => idSet.has(t._id));
    const nowIso = new Date().toISOString();
    const optimisticApproved = sourceRows.map(t => ({
      _id: '__optim_' + t._id,
      _ts: t._ts || null,
      _source: 'google-approved',
      _decidedAt: nowIso,
      _decidedBy: 'you (pending sync)',
      _googleId: t._id,
      _sheetSource: true,
      date: t.date, arrivalTime: t.arrivalTime, licensePlate: t.licensePlate,
      customer: t.customer, site: t.site, contractor: t.contractor,
      truckNumber: t.truckNumber, driver: t.driver,
      tankSize: t.tankSize,
      pctFilled: t.pctFilled, pctSolids: t.pctSolids,
      platePhoto: t.platePhoto || null,
      _raw: t._raw || null,
      _issues: t._issues || [],
      _optimistic: true
    }));
    // OPTIMISTIC UPDATE — remove approved rows from local Pending/Unmatched,
    // prepend stub rows to Approved, and bump counts immediately so the UI
    // feels instant. The server-side refresh runs in the background and
    // replaces the stubs with the real cached Excel rows.
    setPending(prev => ({
      ...prev,
      tips: prev.tips.filter(t => !idSet.has(t._id)),
      counts: { ...prev.counts, pending: Math.max(0, (prev.counts.pending||0) - ids.length) }
    }));
    setUnmatched(prev => ({ ...prev, tips: prev.tips.filter(t => !idSet.has(t._id)) }));
    if (appOffset === 0) {
      setAppRows(prev => [...optimisticApproved, ...prev].slice(0, appLimit));
    }
    setAppTotal(prev => prev + optimisticApproved.length);
    setSelected(new Set());
    setBusy(true);
    try {
      const r = await postJson('/api/google/approve', { ids });
      setMsg('Approved ' + (r.approvedCount||0) + ' · already in Approved: ' + (r.alreadyInExcel||0) + ' · newly added: ' + (r.pushedToExcel||0));
      // Quietly refresh in the background — drops the optimistic stubs and
      // pulls the real cached Excel rows.
      setTimeout(() => { refresh().catch(() => {}); }, 100);
    } catch (e) {
      setMsg('Approve failed: ' + e.message + ' — reloading to recover');
      bustTipCaches(); await refresh();
    }
    finally { setBusy(false); }
  }

  // ── What the table shows: sorting, status filter, search, date range, counts per status ──
  function clientCmp(a, b, sort) {
    const { key, dir } = sort;
    const av = a[key], bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av;
    const as = String(key === 'date' ? fmtDate(av) : av).toLowerCase();
    const bs = String(key === 'date' ? fmtDate(bv) : bv).toLowerCase();
    const cmp = as < bs ? -1 : as > bs ? 1 : 0;
    if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
    // Secondary sort when sorting by date: tie-break with arrivalTime in
    // the same direction so chronological order is fully respected
    // (asc = earliest-in-day first, desc = latest-in-day first).
    if (key === 'date') {
      const at = fmtTime(a.arrivalTime || '');
      const bt = fmtTime(b.arrivalTime || '');
      const tcmp = at < bt ? -1 : at > bt ? 1 : 0;
      return dir === 'asc' ? tcmp : -tcmp;
    }
    return 0;
  }
  const sortedPending = useMemo(() => [...pending.tips].sort((a,b) => clientCmp(a,b,pendSort)), [pending.tips, pendSort]);
  const sortedUnmatched = useMemo(() => [...unmatched.tips].sort((a,b) => clientCmp(a,b,pendSort)), [unmatched.tips, pendSort]);

  // The status-filter UI used to be gated to pending/unmatched. Approved
  // now uses it too (server-side via /api/tips/list?issue=X). We split the
  // gate into two: isReviewable still controls bulk-approve / selection
  // behavior; canFilterByStatus controls the filter UI visibility.
  const isReviewable = tab === 'pending' || tab === 'unmatched';
  const canFilterByStatus = tab === 'pending' || tab === 'unmatched' || tab === 'approved';
  function rowMatchesStatus(r, filter) {
    const issues = r._issues || [];
    if (filter === 'all') return true;
    if (filter === 'clean') return issues.length === 0;
    if (filter === 'warning') return issues.length > 0 && !issues.some(i => CRITICAL.has(i));
    if (filter === 'critical') return issues.some(i => CRITICAL.has(i));
    if (filter === 'prior-approved') {
      // Mismatch rows where the same plate+customer combo was approved before.
      // Treated as 'safe to bulk approve' — likely a known operator shorthand
      // for a customer that's already in production billing under that name.
      return issues.includes('plate_customer_mismatch') && (r._priorApprovals || 0) > 0;
    }
    // specific issue key
    return issues.includes(filter);
  }
  // Free-text search across every visible field on the row. Case-insensitive
  // substring match against every string the user might recognize.
  function rowMatchesSearch(r, q) {
    if (!q) return true;
    const needle = q.toLowerCase();
    const haystack = [
      r.licensePlate, r.customer, r.site, r.contractor, r.truckNumber, r.driver,
      r.date, r.arrivalTime,
      r.tankSize, r.pctFilled, r.pctSolids,
      r._source, r._decidedBy, r._registeredCustomer
    ];
    // Also include any raw form fields the COLS row shows as 'raw' (afterHours, waterSource, classification, etc).
    if (r._raw) {
      for (const v of Object.values(r._raw)) {
        if (v != null) haystack.push(v);
      }
    }
    // Issue keys + labels (so user can search 'unknown' or 'plate ≠ customer')
    if (r._issues && r._issues.length) {
      for (const i of r._issues) {
        haystack.push(i);
        if (ISSUE_LABEL[i]) haystack.push(ISSUE_LABEL[i]);
      }
    }
    for (const v of haystack) {
      if (v != null && String(v).toLowerCase().includes(needle)) return true;
    }
    return false;
  }
  const rawVisible = tab === 'pending' ? sortedPending : tab === 'unmatched' ? sortedUnmatched : appRows;
  const afterStatus = isReviewable ? rawVisible.filter(r => rowMatchesStatus(r, statusFilter)) : rawVisible;
  // Client-side date range for Pending / Unmatched. Approved has its
  // own server-side from/to (appFrom/appTo) so we skip it here.
  const rowInDateRange = (r) => {
    if (!isReviewable) return true;
    const d = r.date ? String(r.date).slice(0, 10) : '';
    if (pendFrom && (!d || d < pendFrom)) return false;
    if (pendTo   && (!d || d > pendTo))   return false;
    return true;
  };
  const afterDate  = (pendFrom || pendTo) ? afterStatus.filter(rowInDateRange) : afterStatus;
  const visibleRows = searchText.trim() ? afterDate.filter(r => rowMatchesSearch(r, searchText.trim())) : afterDate;
  // For counts shown on the filter buttons, run the unfiltered list once
  const statusCounts = useMemo(() => {
    // Approved tab: use server-computed counts (cover the FULL result set,
    // not just the current page). Pending/unmatched: compute client-side
    // from the in-memory rows (same as before).
    if (tab === 'approved') {
      return appStatusCounts
        ? { ...appStatusCounts, all: appTotal, priorApproved: 0 }
        : null;
    }
    if (!isReviewable) return null;
    const c = { all: rawVisible.length, clean: 0, warning: 0, critical: 0, priorApproved: 0, byIssue: {} };
    for (const r of rawVisible) {
      const issues = r._issues || [];
      if (issues.length === 0) c.clean += 1;
      else if (issues.some(i => CRITICAL.has(i))) c.critical += 1;
      else c.warning += 1;
      if (issues.includes('plate_customer_mismatch') && (r._priorApprovals || 0) > 0) c.priorApproved += 1;
      for (const i of issues) c.byIssue[i] = (c.byIssue[i] || 0) + 1;
    }
    return c;
  }, [rawVisible, isReviewable, tab, appStatusCounts, appTotal]);
  const visibleCols  = COLS.filter(c => isReviewable || !c.pendingOrUnmatched);

  // ── Export the current view to Excel ──
  // Export current view (post-filter, post-sort) to Excel. Mirrors what's
  // on screen: same columns, same order, same rows. Excludes the platePhoto
  // column (it's a link, not useful in a spreadsheet). Backend endpoint
  // /api/tips/export-xlsx does the workbook build.
  const [exportBusy, setExportBusy] = useState(false);
  async function handleExportXlsx() {
    if (exportBusy) return;
    // Approved tab is SERVER-paginated -- visibleRows is only the current
    // page (~100 of possibly 29K rows). Exporting that silently truncated.
    // Rule (Suresh 2026-08-20): the Approved export REQUIRES a date range,
    // then pages through the server to export EVERY tip in that range
    // (honouring the active search + status filter).
    let sourceRows = visibleRows;
    if (tab === 'approved') {
      if (!appFrom || !appTo) {
        setMsg('Set both From and To dates above before exporting the Approved tab — the export covers every tip in that range, not just this page.');
        return;
      }
      setExportBusy(true);
      try {
        const all = [];
        const PAGE = 2000;                    // server clamp
        for (let off = 0; off < 60000; off += PAGE) {
          const qs = new URLSearchParams({ offset: String(off), limit: String(PAGE), sort: appSort.key, dir: appSort.dir, from: appFrom, to: appTo });
          const searchTerm = (searchText || '').trim();
          if (searchTerm) qs.set('q', searchTerm);
          if (statusFilter && statusFilter !== 'all') qs.set('issue', statusFilter);
          const a = await getJson('/api/tips/list?' + qs.toString());
          all.push(...(a.rows || []));
          if (all.length >= (a.total || 0) || (a.rows || []).length === 0) break;
        }
        sourceRows = all;
      } catch (e) {
        setMsg('Export fetch failed: ' + e.message);
        setExportBusy(false);
        return;
      }
      setExportBusy(false);
    }
    setExportBusy(true);
    try {
      const tabLabel = tab === 'approved' ? 'Approved' : (tab === 'unmatched' ? 'Unmatched' : 'Pending Review');
      const exportCols = visibleCols
        .filter(c => c.key !== 'platePhoto')   // link col; skip in xlsx
        .map(c => ({ key: c.key, label: c.label, num: !!c.num }));
      const exportRows = sourceRows.map(r => {
        const out = {};
        for (const c of exportCols) {
          let v;
          if (c.key === '_issues') {
            v = (r._issues || []).map(i => ISSUE_LABEL[i] || i).join(', ');
          } else if (c.key === 'date') {
            v = fmtDate(r.date);
          } else if (c.key === 'arrivalTime') {
            v = fmtTime(r.arrivalTime);
          } else if (c.key === '_decidedAt') {
            v = r._decidedAt ? String(r._decidedAt).slice(0, 19).replace('T', ' ') : '';
            if (r._decidedBy) v = v ? v + ' by ' + r._decidedBy : ('by ' + r._decidedBy);
          } else if (c.key === 'hydrovacCustomer' || c.key === 'notes' || c.key === 'tankSize') {
            // Computed columns: no matching row property — the table derives
            // them from _raw via cellValue's matchers. Exporting r[c.key]
            // produced blank cells (bug: empty Hydrovac customer column).
            v = cellValue(r, { key: c.key });
          } else {
            // For 'raw' columns (afterHours, waterSource, etc.), pull from
            // _raw using the same field-name the table uses.
            const colDef = COLS.find(x => x.key === c.key);
            if (colDef && colDef.raw && r._raw) v = r._raw[colDef.raw];
            else v = r[c.key];
          }
          out[c.key] = v == null ? '' : v;
        }
        return out;
      });
      // Filter summary — reflect the visible controls so the exporter can
      // remember what they exported.
      const parts = [];
      if (statusFilter && statusFilter !== 'all') parts.push('status=' + statusFilter);
      if (tab === 'approved') {
        parts.push('from=' + appFrom, 'to=' + appTo, 'full-range export (' + sourceRows.length + ' rows)');
      } else {
        if (pendFrom) parts.push('from=' + pendFrom);
        if (pendTo)   parts.push('to=' + pendTo);
      }
      if (searchText.trim()) parts.push('search="' + searchText.trim() + '"');
      const filterSummary = parts.length ? parts.join(' · ') : '(none)';

      const res = await fetch('/api/tips/export-xlsx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tabName: tabLabel, columns: exportCols, rows: exportRows, filterSummary })
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(res.status + ' ' + txt);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = tabLabel.replace(/[^A-Za-z0-9]+/g, '_') + '_' + new Date().toISOString().slice(0, 10) + '.xlsx';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMsg('Exported ' + exportRows.length + ' rows to ' + a.download);
    } catch (e) {
      setMsg('Export failed: ' + e.message);
    } finally {
      setExportBusy(false);
    }
  }

  // ── Table helpers: sort headers, row selection, paging, cell values ──
  function toggleSort(key) {
    if (isReviewable) setPendSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));
    else { setAppSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' })); setAppOffset(0); }
  }
  const activeSort = isReviewable ? pendSort : appSort;
  const sortArrow = (k) => activeSort.key !== k ? '' : activeSort.dir === 'asc' ? ' ▲' : ' ▼';

  const allIds = visibleRows.map(r => r._id).filter(Boolean);
  // Selection enabled on ALL tabs (2026-08-20: approved too, so bulk
  // project assignment works on approved tips). Approve/bulk-edit
  // actions stay gated to reviewable tabs below.
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
  function toggle(id) { const next = new Set(selected); next.has(id) ? next.delete(id) : next.add(id); setSelected(next); }
  function toggleAll() { setSelected(allSelected ? new Set() : new Set(allIds)); }
  const lastOffset = Math.max(0, Math.floor((appTotal - 1) / appLimit) * appLimit);
  const gotoPage = (off) => setAppOffset(Math.max(0, Math.min(off, lastOffset)));

  function cellValue(r, c) {
    if (c.raw) return r._raw ? r._raw[c.raw] : '';
    if (c.key === 'tankSize') {
      // Show what the operator typed on the form. Do NOT fall back to the
      // registry — if they left it blank, the user wants to SEE that blank
      // and correct it via Review. tank_size_mismatch issue still flags
      // when both are present but differ (separate concern).
      if (r.tankSize != null && r.tankSize !== '') return r.tankSize;
      return null;
    }
    if (c.key === 'hydrovacCustomer') {
      const raw = r._raw || {};
      // Robust matcher: any _raw key containing both 'hydrovac' and 'customer'.
      // Dedup values so we don't show the same string twice.
      const seen = new Set();
      for (const k of Object.keys(raw)) {
        const lk = String(k).toLowerCase();
        if (lk.includes('hydrovac') && lk.includes('customer')) {
          const v = raw[k] != null ? String(raw[k]).trim() : '';
          if (v) seen.add(v);
        }
      }
      return Array.from(seen).join(' · ');
    }
    if (c.key === 'notes') {
      const raw = r._raw || {};
      // Sheet column F: 'IF "OTHER" PLEASE ENTER COMPANY NAME AND PHONE NUMBER'
      //   — despite the header, operators use this field for job#, PO#,
      //   contractor notes ('A&J contracted to Diverso'), etc.
      // Sheet column G: 'Contractor' — the actual hauling company.
      // Robust matcher: any key containing 'OTHER' (catches header drift),
      // falls back to direct match if not found.
      let f = '';
      for (const k of Object.keys(raw)) {
        if (k && k.toUpperCase().includes('OTHER') && k.toUpperCase().includes('COMPANY')) {
          if (raw[k] != null && String(raw[k]).trim()) { f = String(raw[k]).trim(); break; }
        }
      }
      const g = raw['Contractor'] || r.contractor || '';
      return [f, String(g).trim()].filter(Boolean).join(' · ');
    }
    return r[c.key];
  }

  // ── Add-plate pop-up: pre-fill from the raw sheet row ──
  // When opening the Add-Plate modal, prefer the RAW Google Sheet values
  // for every field — that's the source of truth. sheets.js parseRow()
  // already pulled values into r.licensePlate / r.customer / r.tankSize /
  // r.truckNumber via the HEADER_MAP, but if the form's column header
  // drifted at all those parsed values are blank even though the data is
  // right there in r._raw. We scan _raw with fuzzy regex matchers (header
  // name containing 'plate' etc.) and only fall back to the parsed values
  // when _raw has no match for that field.
  function enrichForAddPlate(r) {
    function firstRawMatch(regex) {
      if (!r._raw) return null;
      for (const [k, v] of Object.entries(r._raw)) {
        if (regex.test(k) && v != null && String(v).trim() !== '') return String(v).trim();
      }
      return null;
    }
    // If this row was already corrected via the Review tab, the TOP-LEVEL
    // fields are the truth — _raw still has the operator's original wrong
    // entry. Prefer corrected values over raw for any field the reviewer
    // can change.
    const wasCorrected = !!(r._correctedAt || r._originalFields);
    const correctedFields = (r._originalFields && typeof r._originalFields === 'object')
      ? r._originalFields : {};
    function correctedPreferred(fieldName, rawValue, parsedValue) {
      // If the field appears in _originalFields, the user changed it during
      // correction → use the parsed (= corrected) value, NOT raw.
      if (wasCorrected && Object.prototype.hasOwnProperty.call(correctedFields, fieldName)) {
        return parsedValue;
      }
      // Otherwise standard precedence: raw fuzzy-match → parsed fallback.
      return rawValue != null && rawValue !== '' ? rawValue : parsedValue;
    }
    // Plate: 'License Plate:' but NOT 'License Plate Photo'
    const plateRaw = firstRawMatch(/plate(?!.*photo)/i);
    // Customer: ALWAYS comes from r.customer — that's column E (index 4)
    // of the Google sheet, per the strict positional rule in sheets.js
    // parseRow (set 2026-06-19). We do NOT scan _raw for customer-ish
    // header text because the actual column-E header may not contain
    // 'customer' at all.
    // Truck number: 'Truck Number' — careful not to match 'Truck Company Name'
    const truckRaw = firstRawMatch(/truck\s*number|^truck\s*#|^truck\s*no\b/i);
    // Tank size: any header mentioning 'tank' — parse first numeric portion
    let tankRaw = null;
    const tankStr = firstRawMatch(/tank/i);
    if (tankStr) {
      const m = tankStr.match(/(\d+(\.\d+)?)/);
      if (m) tankRaw = Number(m[1]);
    }
    const plateValue = correctedPreferred('licensePlate', plateRaw, r.licensePlate);
    const customerValue = correctedPreferred('customer', null, r.customer);
    const tankValue = correctedPreferred('tankSize', tankRaw, r.tankSize);
    return {
      ...r,
      licensePlate: String(plateValue || '').toUpperCase().replace(/\s+/g, ''),
      customer:     String(customerValue || ''),
      truckNumber:  String(truckRaw || r.truckNumber || ''),
      tankSize:     tankValue != null && tankValue !== '' ? tankValue : '',
      // Original parser-extracted plate at modal-open time — used by
      // confirmAddPlate to also clear unknown_plate on the source row
      // even if the user typed a corrected plate in the modal. Always
      // the CURRENT top-level licensePlate (which == corrected if the
      // row was corrected) so we clear the issue on the correct rows.
      _origPlate: String(r.licensePlate || '').toUpperCase().replace(/\s+/g, '')
    };
  }

  return (
    <div className="page">
      {/* Page title (with approved / sheet-inbox counts for admins) and the Reload button. */}
      <PageHeader appTotal={appTotal} busy={busy} inboxCount={inboxCount} me={me} pending={pending} refresh={refresh} refreshing={refreshing} unmatched={unmatched} />

      {msg && <div className="card" style={{ padding: '0.6rem 1rem', marginBottom: '0.5rem', background: '#f0f9ff', color: '#0c4a6e' }}>{msg}</div>}

      {/* Admin-only tools strip. */}
      <AdminToolsCard appTotal={appTotal} me={me} pending={pending} />
      {/* Admin-only: Google Sheet vs database row counts per month (flags rows the sync missed). */}
      <CoverageCard coverage={coverage} coverageError={coverageError} coverageLoading={coverageLoading} coverageOpen={coverageOpen} loadCoverage={loadCoverage} me={me} setCoverageOpen={setCoverageOpen} />

      {/* Tabs: Pending review / Approved / Unmatched. */}
      <ReviewTabs appTotal={appTotal} me={me} pending={pending} setSelected={setSelected} setTab={setTab} tab={tab} unmatched={unmatched} />

      {/* Free-text search across every visible column. */}
      <SearchBar exportBusy={exportBusy} handleExportXlsx={handleExportXlsx} isReviewable={isReviewable} rawVisible={rawVisible} searchText={searchText} setAppOffset={setAppOffset} setSearchText={setSearchText} setSelected={setSelected} statusFilter={statusFilter} visibleRows={visibleRows} />
      {/* Status filter buttons (All / Clean / Warning / Critical / each issue type) with counts. */}
      <StatusFilters busy={busy} canFilterByStatus={canFilterByStatus} setSelected={setSelected} setStatusFilter={setStatusFilter} statusCounts={statusCounts} statusFilter={statusFilter} />
      {/* Date range filter for the Pending and Unmatched tabs. */}
      <PendingDateRange afterDate={afterDate} afterStatus={afterStatus} busy={busy} isReviewable={isReviewable} pendFrom={pendFrom} pendTo={pendTo} refresh={refresh} setPendFrom={setPendFrom} setPendTo={setPendTo} setSelected={setSelected} />
      {/* Selected-rows bar: approve selected, bulk edit, assign project, export to Excel, undo last bulk edit. */}
      <SelectionBar bulkAssignProject={bulkAssignProject} busy={busy} canFilterByStatus={canFilterByStatus} canWrite={canWrite} handleApproveSelected={handleApproveSelected} isReviewable={isReviewable} lastBulkBatch={lastBulkBatch} pending={pending} projBusy={projBusy} rawVisible={rawVisible} refresh={refresh} selected={selected} setBulkCustomer={setBulkCustomer} setBulkOpen={setBulkOpen} setBulkReason={setBulkReason} setBulkTankSize={setBulkTankSize} setLastBulkBatch={setLastBulkBatch} setMsg={setMsg} setSelected={setSelected} setStatusFilter={setStatusFilter} setUndoBusy={setUndoBusy} statusCounts={statusCounts} statusFilter={statusFilter} tab={tab} undoBusy={undoBusy} visibleRows={visibleRows} />

      {/* Approved tab: date range + paging (100 rows per page, loaded from the server). */}
      <ApprovedPager appFrom={appFrom} appLimit={appLimit} appOffset={appOffset} appTo={appTo} appTotal={appTotal} busy={busy} gotoPage={gotoPage} lastOffset={lastOffset} refresh={refresh} setAppFrom={setAppFrom} setAppOffset={setAppOffset} setAppTo={setAppTo} tab={tab} />

      {/* The tips table itself: sortable headers, one row per tip, issue colouring, photo / edit / history buttons. */}
      <TipsTable allSelected={allSelected} assignProject={assignProject} camProbable={camProbable} camSeen={camSeen} canWrite={canWrite} cellValue={cellValue} enrichForAddPlate={enrichForAddPlate} isReviewable={isReviewable} me={me} openApprovedEditor={openApprovedEditor} openEditor={openEditor} openHistory={openHistory} projBusy={projBusy} projects={projects} projectsByCustomer={projectsByCustomer} projectsById={projectsById} selected={selected} setAddPlateRow={setAddPlateRow} setHoverPhoto={setHoverPhoto} setPhotoRow={setPhotoRow} setRawRow={setRawRow} setUnapproveReason={setUnapproveReason} setUnapproveRow={setUnapproveRow} sortArrow={sortArrow} tab={tab} toggle={toggle} toggleAll={toggleAll} toggleSort={toggleSort} visibleCols={visibleCols} visibleRows={visibleRows} />

      {/* Pop-up: the driver's plate photo for a tip. */}
      <PhotoModal photoRow={photoRow} refresh={refresh} setPhotoRow={setPhotoRow} />

      {/* Pop-up: correct a pending tip (plate / customer / tank size) before approval. */}
      <CorrectionModal busy={busy} editFields={editFields} editRow={editRow} refLists={refLists} refresh={refresh} saveCorrection={saveCorrection} setEditFields={setEditFields} setEditRow={setEditRow} tab={tab} />

      {/* Pop-up: the raw Google-sheet row behind a tip. */}
      <RawRowModal rawRow={rawRow} refresh={refresh} setRawRow={setRawRow} />

      {/* Admin-only Danger Zone: full reset (opens the typed-confirmation pop-up). */}
      <DangerZone busy={busy} me={me} openResetModal={openResetModal} refresh={refresh} />

      {/* Pop-up: full reset with typed confirmation and an automatic snapshot first. */}
      <ResetModal busy={busy} refresh={refresh} resetModal={resetModal} resetSnapshot={resetSnapshot} resetTyped={resetTyped} runReset={runReset} setResetModal={setResetModal} setResetTyped={setResetTyped} tab={tab} />

      {/* Pop-up: register an unknown plate to a customer (pre-filled from the sheet row). */}
      <AddPlateModal addPlateBusy={addPlateBusy} addPlateRow={addPlateRow} confirmAddPlate={confirmAddPlate} pending={pending} refLists={refLists} refresh={refresh} setAddPlateBusy={setAddPlateBusy} setAddPlateRow={setAddPlateRow} />

      {/* Pop-up: edit an already-approved tip (reason required; recorded in history). */}
      <EditApprovedModal editApprovedBusy={editApprovedBusy} editApprovedFields={editApprovedFields} editApprovedReason={editApprovedReason} editApprovedRow={editApprovedRow} refLists={refLists} refresh={refresh} saveApprovedEdit={saveApprovedEdit} setEditApprovedFields={setEditApprovedFields} setEditApprovedReason={setEditApprovedReason} setEditApprovedRow={setEditApprovedRow} />

      {/* Pop-up: change history (audit log) for one tip. */}
      <HistoryModal historyBusy={historyBusy} historyEntries={historyEntries} historyRow={historyRow} refresh={refresh} setHistoryRow={setHistoryRow} />

      {/* Pop-up: Google-sheet changes waiting to be applied to approved tips. */}
      <InboxModal inboxBusy={inboxBusy} inboxEntries={inboxEntries} inboxOpen={inboxOpen} refresh={refresh} setInboxOpen={setInboxOpen} />

      {/* Pop-up: change customer / tank size on all selected tips at once (one-click undo). */}
      <BulkEditModal bulkBusy={bulkBusy} bulkCustomer={bulkCustomer} bulkOpen={bulkOpen} bulkReason={bulkReason} bulkTankSize={bulkTankSize} refLists={refLists} refresh={refresh} selected={selected} setBulkBusy={setBulkBusy} setBulkCustomer={setBulkCustomer} setBulkOpen={setBulkOpen} setBulkReason={setBulkReason} setBulkTankSize={setBulkTankSize} setLastBulkBatch={setLastBulkBatch} setMsg={setMsg} setSelected={setSelected} />

      {/* Pop-up: send an approved tip back to Pending (reason required). */}
      <UnapproveModal confirmUnapprove={confirmUnapprove} refresh={refresh} setUnapproveReason={setUnapproveReason} setUnapproveRow={setUnapproveRow} unapproveBusy={unapproveBusy} unapproveReason={unapproveReason} unapproveRow={unapproveRow} />

      {/* Small floating photo preview that follows the cursor. */}
      <HoverPreview hoverPhoto={hoverPhoto} />
    </div>
  );
}
